import { useState, useEffect } from 'react'
import '../styles/PWAInstallPrompt.css'

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    // Check if running in standalone mode (already installed)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://')

    setIsStandalone(standalone)

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    setIsIOS(iOS)

    // Check if user has previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa_install_dismissed')
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0
    const weekInMs = 7 * 24 * 60 * 60 * 1000
    const shouldShowAgain = Date.now() - dismissedTime > weekInMs

    // For Android/Desktop - listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault()
      // Stash the event so it can be triggered later
      setDeferredPrompt(e)

      // Show the install prompt if not dismissed recently
      if (!standalone && shouldShowAgain) {
        setShowPrompt(true)
      }
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // For iOS - show manual instructions if not installed and not recently dismissed
    if (iOS && !standalone && shouldShowAgain) {
      // Show iOS install prompt after a short delay
      setTimeout(() => {
        setShowPrompt(true)
      }, 3000)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return
    }

    // Show the install prompt
    deferredPrompt.prompt()

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice

    console.log(`User response to install prompt: ${outcome}`)

    // Clear the deferred prompt
    setDeferredPrompt(null)
    setShowPrompt(false)

    if (outcome === 'dismissed') {
      // User dismissed, don't show again for a week
      localStorage.setItem('pwa_install_dismissed', Date.now().toString())
    }
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    localStorage.setItem('pwa_install_dismissed', Date.now().toString())
  }

  // Don't show if already installed
  if (isStandalone || !showPrompt) {
    return null
  }

  return (
    <div className="pwa-install-prompt">
      <div className="pwa-install-content">
        <button className="pwa-close-btn" onClick={handleDismiss} aria-label="Close">
          ✕
        </button>

        <div className="pwa-install-icon">
          <img src="/hdcc-logo.ico" alt="HDCC Logo" />
        </div>

        <div className="pwa-install-text">
          <h3>Install HDCC Portal</h3>
          {isIOS ? (
            <p>
              Tap <span className="ios-share-icon">
                <svg width="16" height="20" viewBox="0 0 16 20" fill="currentColor">
                  <path d="M8 0L3.5 4.5L5.25 6.25L7 4.5V13H9V4.5L10.75 6.25L12.5 4.5L8 0Z"/>
                  <path d="M0 8V18C0 19.1 0.9 20 2 20H14C15.1 20 16 19.1 16 18V8H14V18H2V8H0Z"/>
                </svg>
              </span> then "Add to Home Screen" to install this app
            </p>
          ) : (
            <p>Install this app on your device for a better experience and offline access</p>
          )}
        </div>

        {!isIOS && deferredPrompt && (
          <button className="pwa-install-btn" onClick={handleInstallClick}>
            Install App
          </button>
        )}
      </div>
    </div>
  )
}

export default PWAInstallPrompt
